import { useRef, useState } from 'react'

import { useCreateProduct, useImportCsv } from '../../lib/admin'
import type { AdminStore, CsvImportResult } from '../../lib/admintypes'
import { useSlots } from '../../lib/workspace'

/**
 * Getting products in by hand (06 §8).
 *
 * Two of ASB's four suppliers cannot be crawled — Builders Architectural Millwork posts no
 * prices at all, and a licensed feed is not always available. Without this screen those
 * suppliers are simply absent from the studio, which is the opposite of the point: the
 * millwork supplier is the one Andrew actually uses for trim.
 *
 * Two doors, because the two jobs are different:
 *
 *  * **One product**, typed in — the case where somebody is standing in a supplier's yard with
 *    a phone and a price tag in front of them. Short form, big targets, approved by default,
 *    because a product entered by hand is one somebody already decided to sell.
 *  * **A spreadsheet** — the case where a rep emails a price list. A row that is wrong is
 *    reported by line number and skipped; the rest still import. A supplier's spreadsheet
 *    always has one bad line, and losing the other four hundred over it is not acceptable.
 *
 * Both write `source = manual`, which is what stops a later crawl overwriting a price a person
 * typed (06 §8).
 */
export function ProductEntry({ stores }: { stores: AdminStore[] }) {
  const [open, setOpen] = useState<'none' | 'one' | 'csv'>('none')

  return (
    <section className="mt-6 rounded-card border border-line bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div>
          <h2 className="m-0 text-base">Add products by hand</h2>
          <p className="m-0 mt-0.5 text-sm text-muted">
            For suppliers with no scrapable site — millwork, or a price list that arrived by email.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            aria-pressed={open === 'one'}
            onClick={() => setOpen(open === 'one' ? 'none' : 'one')}
            className={`min-h-11 rounded-pill border px-4 text-sm font-semibold transition-colors ${
              open === 'one'
                ? 'border-navy bg-navy text-white'
                : 'border-line bg-white text-ink hover:border-blue'
            }`}
          >
            One product
          </button>
          <button
            type="button"
            aria-pressed={open === 'csv'}
            onClick={() => setOpen(open === 'csv' ? 'none' : 'csv')}
            className={`min-h-11 rounded-pill border px-4 text-sm font-semibold transition-colors ${
              open === 'csv'
                ? 'border-navy bg-navy text-white'
                : 'border-line bg-white text-ink hover:border-blue'
            }`}
          >
            Import a CSV
          </button>
        </div>
      </div>

      {open === 'one' && <SingleProductForm stores={stores} />}
      {open === 'csv' && <CsvForm stores={stores} />}
    </section>
  )
}

const UNITS = [
  ['each', 'each'],
  ['sqft', 'per sq ft'],
  ['lnft', 'per linear ft'],
  ['hour', 'per hour'],
] as const

function SingleProductForm({ stores }: { stores: AdminStore[] }) {
  const create = useCreateProduct()
  const slots = useSlots()
  const [storeKey, setStoreKey] = useState(stores[0]?.key ?? '')
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [brand, setBrand] = useState('')
  const [price, setPrice] = useState('')
  const [unit, setUnit] = useState<string>('each')
  const [imageUrl, setImageUrl] = useState('')
  const [slot, setSlot] = useState('')
  const [done, setDone] = useState<string | null>(null)

  const problem = create.error ? String((create.error as Error).message) : null

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setDone(null)
    const created = await create.mutateAsync({
      store_key: storeKey,
      name: name.trim(),
      url: url.trim(),
      price: Number(price),
      unit,
      brand: brand.trim() || null,
      image_url: imageUrl.trim() || null,
      slots: slot ? [slot] : [],
      whitelisted: true,
    })
    setDone(created.name)
    // Everything except the store and the slot: the next product is nearly always from the
    // same supplier for the same slot, and re-picking both forty times is the whole friction.
    setName('')
    setUrl('')
    setBrand('')
    setPrice('')
    setImageUrl('')
  }

  return (
    <form onSubmit={submit} className="border-t border-line px-4 py-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Supplier">
          <select
            value={storeKey}
            onChange={(event) => setStoreKey(event.target.value)}
            required
            className="min-h-11 w-full rounded-btn border border-line px-3 text-sm"
          >
            {stores.map((store) => (
              <option key={store.key} value={store.key}>
                {store.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Slot" hint="A product with no slot never appears in a rail.">
          <select
            value={slot}
            onChange={(event) => setSlot(event.target.value)}
            className="min-h-11 w-full rounded-btn border border-line px-3 text-sm"
          >
            <option value="">No slot yet</option>
            {(slots.data ?? []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name} ({entry.id})
              </option>
            ))}
          </select>
        </Field>

        <Field label="Name" className="sm:col-span-2">
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            minLength={2}
            maxLength={400}
            placeholder="Colonial casing, 3-1/4 in. primed pine"
            className="min-h-11 w-full rounded-btn border border-line px-3 text-sm"
          />
        </Field>

        <Field
          label="Link"
          hint="The supplier's page, or a catalogue PDF. It is also the row's identity."
          className="sm:col-span-2"
        >
          <input
            type="url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            required
            placeholder="https://…"
            className="min-h-11 w-full rounded-btn border border-line px-3 text-sm"
          />
        </Field>

        <Field label="Brand">
          <input
            value={brand}
            onChange={(event) => setBrand(event.target.value)}
            className="min-h-11 w-full rounded-btn border border-line px-3 text-sm"
          />
        </Field>

        <Field label="Image link" hint="Optional, but the rail hides products with no picture.">
          <input
            type="url"
            value={imageUrl}
            onChange={(event) => setImageUrl(event.target.value)}
            className="min-h-11 w-full rounded-btn border border-line px-3 text-sm"
          />
        </Field>

        <Field label="Material price">
          <div className="flex gap-2">
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              required
              className="min-h-11 w-full rounded-btn border border-line px-3 text-sm tabular-nums"
            />
            <select
              value={unit}
              onChange={(event) => setUnit(event.target.value)}
              aria-label="Unit"
              className="min-h-11 rounded-btn border border-line px-2 text-sm"
            >
              {UNITS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </Field>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={create.isPending}
          className="min-h-11 rounded-pill bg-navy px-5 text-sm font-semibold text-white disabled:opacity-60"
        >
          {create.isPending ? 'Saving…' : 'Add and approve'}
        </button>
        <p className="m-0 text-sm text-muted">
          Approved on save — something typed in by hand was already decided on.
        </p>
      </div>

      {done && (
        <p className="mt-3 text-sm text-ink" role="status">
          Added <strong>{done}</strong>. The form is ready for the next one.
        </p>
      )}
      {problem && (
        <p className="m-0 mt-3 rounded-btn bg-blue-tint px-3.5 py-3 text-sm text-ink" role="alert">
          {problem}
        </p>
      )}
    </form>
  )
}

function CsvForm({ stores }: { stores: AdminStore[] }) {
  const load = useImportCsv()
  const [storeKey, setStoreKey] = useState(stores[0]?.key ?? '')
  const [whitelist, setWhitelist] = useState(true)
  const [result, setResult] = useState<CsvImportResult | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const problem = load.error ? String((load.error as Error).message) : null

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    const file = fileRef.current?.files?.[0]
    if (!file) return
    setResult(await load.mutateAsync({ file, store: storeKey, whitelist }))
  }

  return (
    <form onSubmit={submit} className="border-t border-line px-4 py-4">
      <p className="m-0 max-w-[70ch] text-sm text-muted">
        Columns: <code className="font-mono text-xs">name, url, price, unit</code> are required;{' '}
        <code className="font-mono text-xs">
          brand, description, external_id, image_url, product_type, slots
        </code>{' '}
        are optional. Put several slots in one cell separated by semicolons. Up to 5 MB and 5,000
        rows.
      </p>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="Supplier">
          <select
            value={storeKey}
            onChange={(event) => setStoreKey(event.target.value)}
            required
            className="min-h-11 w-full rounded-btn border border-line px-3 text-sm"
          >
            {stores.map((store) => (
              <option key={store.key} value={store.key}>
                {store.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="File">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            required
            className="min-h-11 w-full rounded-btn border border-line px-3 py-2 text-sm"
          />
        </Field>
      </div>

      <label className="mt-3 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={whitelist}
          onChange={(event) => setWhitelist(event.target.checked)}
          className="size-5"
        />
        Approve every row that imports
      </label>

      <button
        type="submit"
        disabled={load.isPending}
        className="mt-4 min-h-11 rounded-pill bg-navy px-5 text-sm font-semibold text-white disabled:opacity-60"
      >
        {load.isPending ? 'Importing…' : 'Import'}
      </button>

      {result && (
        <div className="mt-4 rounded-card border border-line bg-paper p-3" role="status">
          <p className="m-0 text-sm text-ink">
            {result.created} added, {result.updated} updated, {result.skipped} skipped.
          </p>
          {result.errors.length > 0 && (
            <ul className="mt-2 mb-0 list-disc pl-5 text-sm text-muted">
              {result.errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {problem && (
        <p className="m-0 mt-3 rounded-btn bg-blue-tint px-3.5 py-3 text-sm text-ink" role="alert">
          {problem}
        </p>
      )}
    </form>
  )
}

function Field({
  label,
  hint,
  className = '',
  children,
}: {
  label: string
  hint?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block font-mono text-xs uppercase tracking-[0.12em] text-muted">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
    </label>
  )
}
